import axios, { AxiosRequestConfig } from 'axios'

export const AI_API_TIMEOUTS = {
  // A structured comparison may make one bounded correction call after the
  // initial generation. Keep the browser connected for that backend contract;
  // otherwise it refreshes at 120s while the server is still finishing and the
  // conversation appears to contain only the user's message.
  generationPost: 240_000,
  sessionMessages: 15_000,
  candidateHydration: 10_000,
  offerHydration: 10_000,
  sessionList: 10_000,
} as const

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL
  let baseUrl: string

  if (envUrl && envUrl.trim().length > 0) {
    baseUrl = envUrl.trim()
  } else {
    const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost'
    baseUrl = `http://${host}:8000`
  }

  // Remove trailing slash
  baseUrl = baseUrl.replace(/\/+$/, '')

  // Normalize /api path handling
  const apiPath = '/api'

  // Count how many times /api appears
  const apiMatches = baseUrl.match(new RegExp(apiPath.replace('/', '\\/'), 'g'))
  const apiCount = apiMatches ? apiMatches.length : 0

  // If /api is duplicated (appears more than once), remove all and add one at the end
  if (apiCount > 1) {
    baseUrl = baseUrl.replace(new RegExp(apiPath.replace('/', '\\/') + '/?', 'g'), '')
    baseUrl = baseUrl + apiPath
  }
  // If /api is missing, add it
  else if (apiCount === 0) {
    baseUrl = baseUrl + apiPath
  }
  // If /api exists once but not at the end, ensure it's at the end
  else if (apiCount === 1 && !baseUrl.endsWith(apiPath)) {
    // Remove existing /api and add it at the end
    baseUrl = baseUrl.replace(new RegExp(apiPath.replace('/', '\\/') + '/?', 'g'), '')
    baseUrl = baseUrl + apiPath
  }

  return baseUrl
}

const API_BASE_URL = `${getApiBase()}`

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

async function fetchAllPages<T extends { id?: number | string }>(
  initialPath: string,
  params?: Record<string, any>
): Promise<T[]> {
  const items: T[] = []
  const seenIds = new Set<number | string>()
  let nextUrl: string | null = initialPath

  // Add params to the initial path if provided
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params as any).toString()
    nextUrl = nextUrl.includes('?') ? `${nextUrl}&${queryString}` : `${nextUrl}?${queryString}`
  }

  while (nextUrl) {
    const url =
      nextUrl.startsWith('http') || nextUrl.startsWith('/')
        ? nextUrl
        : `/${nextUrl}`

    const response = await api.get<PaginatedResponse<T>>(url)
    const { results = [], next } = response.data

    for (const item of results) {
      // Guard against unstable pagination returning the same row on multiple pages
      if (item?.id != null) {
        if (seenIds.has(item.id)) continue
        seenIds.add(item.id)
      }
      items.push(item)
    }

    if (next) {
      nextUrl = next.startsWith(API_BASE_URL) ? next.replace(API_BASE_URL, '') : next
    } else {
      nextUrl = null
    }
  }

  return items
}

const PUBLIC_AUTH_PATHS = [
  '/auth/login/',
  '/auth/register/',
  '/auth/token/refresh/',
  '/auth/logout/',
  '/auth/password-reset',
  '/auth/2fa/recovery/',
  '/auth/2fa/verify/',
  '/auth/2fa/verify-backup/',
  '/auth/2fa/request-email-otp/',
]

const PUBLIC_APP_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/recover-2fa',
  '/organization-setup',
  '/interview_audio',
]

function isPublicAuthRequest(url?: string): boolean {
  if (!url) return false
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path))
}

function isPublicAppPath(pathname: string): boolean {
  return PUBLIC_APP_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  )
}

// JWTs live in httpOnly cookies; do not attach them from storage.
api.interceptors.request.use(
  (config) => {
    const language = localStorage.getItem('language') || 'en'
    config.headers['Accept-Language'] = language
    config.withCredentials = true

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

let refreshInFlight: Promise<void> | null = null

function refreshSession(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = api
      .post('/auth/token/refresh/')
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

// Refresh via cookie, then retry. Tokens are never stored in localStorage.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isPublicAuthRequest(originalRequest.url)
    ) {
      originalRequest._retry = true

      try {
        await refreshSession()
        if (originalRequest.headers) {
          delete originalRequest.headers.Authorization
        }
        originalRequest.withCredentials = true
        return api(originalRequest)
      } catch (refreshError) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('selektia_user')
        localStorage.removeItem('selektia_email_connection')
        if (
          typeof window !== 'undefined' &&
          !isPublicAppPath(window.location.pathname)
        ) {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export type CandidateSource = 'internal' | 'job_board' | 'referral' | 'agency' | 'other'

export interface Candidate {
  linkedin_url?: string | null
  github_url?: string | null
  twitter_url?: string | null
  portfolio_url?: string | null
  others_url?: string[] | null
  id: number
  name: string
  full_name?: string
  email: string
  phone?: string | null
  role: string
  status?: string
  source?: CandidateSource
  experience?: string | null
  location?: string | null
  profile_picture?: string | null
  age?: string | null
  date_naissance?: string | null
  genre?: string | null
  skills?: string[]
  soft_skills?: string[]
  main_missions?: string[]
  nice_to_have?: string[]
  certifications?: string[]
  languages?: Array<{ name: string; level?: string | null }>
  education?: string[] | Array<{ diplome?: string; etablissement?: string; annee?: string; niveau_etude?: string }>
  projets?: string[] | Array<{ nom?: string; description?: string; technologies?: string[] }>
  work_history?: string[] | Array<{ poste?: string; entreprise?: string; date_debut?: string; date_fin?: string; missions?: string[] }>
  structured_profile?: any
  bio?: string | null
  admin_comment?: string | null
  applied_date?: string
  last_contact?: string
  created_at?: string
  updated_at?: string
  translation_en?: Record<string, any>
  found_in?: string[]
  career_gaps?: Array<{
    gap_start: string
    gap_end: string
    gap_months_approx: number
    gap_days: number
  }>
  career_intervals?: Array<{
    start: string
    end: string
  }>
  career_analysis_sentences?: string[]
  translation_en?: Record<string, any> | null
}

export type ApplicationStage = 'applied' | 'screening' | 'interview' | 'offer' | 'hired'

export type InterviewQuestionSection =
  | 'questions_communes'
  | 'hard_skills'
  | 'soft_skills'
  | 'experience'
  | 'projets'
  | 'formations'
  | 'certifications'
  | 'langues'
  | 'nice_to_have'

export interface CommonQuestion {
  question: string
  duration: number
  description?: string
  // Evaluation section the question is scored in, weighted by `Offer.section_weights`.
  section?: InterviewQuestionSection
}

export interface OfferHardSkills {
  hard_skills_mentionnées?: string[]
  hard_skills_non_mentionnées?: string[]
}

export interface OfferSoftSkills {
  soft_skills_mentionnées?: string[]
  soft_skills_non_mentionnées?: string[]
}

export interface OfferBenefits {
  nice_to_have_mentionnées?: string[]
  nice_to_have_non_mentionnées?: string[]
}

export type OfferMatchingWeights = {
  languages: number
  hard_skills: number
  soft_skills: number
  nice_to_have: number
  niveau_etude: number
  main_missions: number
  certifications: number
  experience_years: number
}

export interface Offer {
  id: number
  candidate: number
  candidate_details?: Candidate
  job_title: string
  title?: string
  department?: string
  location?: string
  contract_type?: string
  experience_level?: string
  deadline?: string
  interview_deadline?: string | null
  remote_allowed: boolean
  status: 'draft' | 'published' | 'offer_closed' | 'closed' | 'pending' | 'accepted' | 'declined' | 'expired'
  benefits: OfferBenefits
  start_date: string | null
  description: string | null
  description_text?: string | null
  description_text_source?: 'extracted' | 'manual' | 'ai_generated'
  display_description?: string | null
  ai_description?: string | null
  ai_description_used?: boolean
  ai_generated_at?: string | null
  ai_generated_by?: number | null
  requirements: OfferHardSkills
  education_level?: string
  languages?: Array<{ language?: string; name?: string; level?: string }>
  certifications?: string[]
  main_missions?: string[]
  soft_skills?: OfferSoftSkills
  salary?: string
  ai_matching_weights?: OfferMatchingWeights | Record<string, number>
  skill_weights?: {
    hard_skills?: Record<string, number>
    soft_skills?: Record<string, number>
    nice_to_have?: Record<string, number>
  }
  created_date: string
  expiry_date: string | null
  updated_at: string
  is_expired: boolean
  days_until_expiry: number | null
  found_in?: string[]
  total_candidates?: number
  latest_stage?: ApplicationStage | null
  latest_stage_count?: number
  /** Per-step breakdown when offer has custom steps (e.g. 1 in Interview, 1 in Reboot Interview) */
  candidates_by_step?: Array<{ step_name: string; count: number }>
  minimum_score?: number
  minimum_interview_score?: number
  common_questions?: any[]
  section_weights?: Record<string, number>
  total_interview_questions?: number
  interview_questions_per_section?: Record<string, number>
  auto_generate_interviews?: boolean
  manual_interview_questions_created?: boolean
  auto_send_interview_emails?: boolean
  auto_evaluate_interviews?: boolean
  interview_language?: 'fr' | 'en' | 'custom'
  email_language?: 'fr' | 'en'
  filter_matching?: {
    experience?: number
    stage_exclu?: number
    hard_skills?: Record<string, number>
    soft_skills?: Record<string, number>
    nice_to_have?: Record<string, number>
    certifications?: Record<string, number>
    languages?: Record<string, number>
    education_level?: {
      level: string
      similarity: number
    }
    /** Set when the first AI matching apply used Top-K selection */
    initial_matching_mode?: 'score' | 'top_k'
    top_k?: number
  }
  has_matching_run?: boolean
  /** ID of the hiring process template attached to this offer */
  hiring_template?: number | null
  /** Raw AI response from LLM after offer analysis – contains extracted skills */
  ai_response?: Record<string, any> | null
  translation_en?: {
    job_title?: string
    description?: string
    hard_skills?: string[]
    soft_skills?: string[]
    certifications?: string[]
    benefits?: string[]
    languages?: Array<{ language: string; level?: string }>
    main_missions?: string[]
    timestamp?: string
  }
}

export interface Department {
  id: number
  name: string
  created_at: string
}

export type ApplicationStatusCountKey =
  | 'applied'
  | 'scanning'
  | 'screening'
  | 'interview'
  | 'hired'
  | 'rejected'

export interface RecentOfferStatsItem {
  id: number
  job_title: string
  status: string
  created_date: string
  total_applications: number
  by_status: Record<ApplicationStatusCountKey, number>
}

export interface Application {
  id: number
  candidate: number
  candidate_name: string
  candidate_email: string
  candidate_role?: string
  candidate_phone: string | null
  candidate_skills: string[]
  candidate_experience: string | null
  candidate_education: string | null
  candidate_location: string | null
  candidate_profile_picture?: string | null
  offer: number
  offer_title: string
  status: 'applied' | 'scanning' | 'screening' | 'interview' | 'hr_interview' | 'rh_interview' | 'offer' | 'hired' | 'rejected'
  match_score: number
  ai_decision?: 'passed_by_ai' | 'failed_by_ai' | null
  applied_date: string
  status_updated_at: string
  created_at: string
  updated_at: string
  notes: string | null
  cover_letter: string | null
  days_since_applied: number
  is_active: boolean
  current_step?: number | null
}

export interface MatchingJob {
  id: number
  name: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  offer: number
  offer_title: string
  origin?: 'ai_matching' | 'offer_quick' | string
  candidate_ids: number[]
  team_ids: number[]
  results: MatchingResult[]
  total_candidates: number
  processing_time: number
  duration: number
  results_summary: {
    total: number
    high_matches: number
    avg_score: number
    max_score: number
  }
  celery_task_id: string | null
  error_message: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
}

export interface MatchingResultCategory {
  score: number
  weight?: number
  weighted_score?: number
  matched?: string[]
  missing?: string[]
}

export interface MatchingResult {
  candidate: Candidate
  offer: Offer
  match_score: number
  matching_skills: string[]
  reasons: string[]
  category_score?: number
  base_similarity?: number
  weights?: Record<string, number>
  score_breakdown?: Record<string, MatchingResultCategory>
}

// Email Configuration Interfaces
export interface EmailConfiguration {
  id?: number
  smtp_host: string
  smtp_port: number
  smtp_username: string
  smtp_password: string
  from_email: string
  from_name: string
  reply_to?: string
  use_tls: boolean
  use_ssl: boolean
  system_email_language?: 'fr' | 'en'
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export interface EmailTemplate {
  id: number
  name: string
  template_type: string
  language?: 'fr' | 'en'
  subject: string
  body: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface EmailLog {
  id: number
  organization?: number
  organization_name?: string
  sent_by?: number
  sent_by_name?: string
  candidate_id?: number
  source?: string
  batch_id?: string
  to_email: string
  subject: string
  template_type?: string
  status: 'sent' | 'delivered' | 'opened' | 'failed'
  sent_at: string
  delivered_at?: string
  opened_at?: string
  error_message?: string
}

export interface EmailStats {
  total_sent: number
  total_delivered: number
  total_failed: number
  delivery_rate: number
}

export interface EmailTestData {
  to_email: string
  subject?: string
  message?: string
  // Optional: allow testing with config from form (even if not saved)
  smtp_host?: string
  smtp_port?: number
  smtp_username?: string
  smtp_password?: string
  from_email?: string
  from_name?: string
  use_tls?: boolean
  use_ssl?: boolean
}

export interface Upload {
  id: number
  file: string
  original_filename: string
  file_type: string
  file_size: number
  extracted_text?: string
  processed_data?: any
  status: 'uploaded' | 'processing' | 'processed' | 'failed'
  error_message?: string
  uploaded_at: string
  processed_at?: string
  uploaded_by: number
  candidate?: number
  candidate_details?: Candidate
  file_url?: string
}

export interface Job {
  id: number
  title: string
  location: string
  type: string
  description: string
  requirements: string[]
  benefits: string[]
  status: string
  createdDate: string
  expiryDate: string
}

export interface Activity {
  id: number
  type: string
  candidateId?: number
  candidateName?: string
  offerId?: number
  oldStatus?: string
  newStatus?: string
  timestamp: string
  description: string
}

export interface User {
  id: number
  email: string
  username?: string
  first_name: string
  last_name: string
  full_name: string
  company?: string
  phone?: string
  calendly_link?: string
  is_recruiter: boolean
  is_active: boolean
  is_staff?: boolean
  is_superuser?: boolean
  date_joined: string
  avatar?: string
  avatar_url?: string
  organization?: number | Organization | null
}

export interface NotificationPreferences {
  email: boolean
  push: boolean
  weekly_reports: boolean
  matching_updates: boolean
  candidate_updates: boolean
  offer_updates: boolean
}

export interface TeamOfferSummary {
  id: number
  job_title: string
  status: Offer['status']
}

export interface Team {
  id: number
  name: string
  description?: string
  organization?: number | Organization | null
  color: string
  created_at: string
  updated_at: string
  created_by?: number
  candidates?: Candidate[]
  offers?: TeamOfferSummary[]
  candidate_ids?: number[] // For updates
  offer_ids?: number[] // For updates
  is_archived?: boolean
  archived_at?: string | null
}

export interface TeamPayload {
  name: string
  description?: string
  color: string
  candidate_ids?: number[]
  offer_ids?: number[]
  is_archived?: boolean
}

export interface Organization {
  id: number
  name: string
  description?: string
  domain: string
  website?: string
  logo?: string
  email?: string
  phone?: string
  address?: string
  status?: 'active' | 'suspended' | 'cancelled' | 'payment_failed' | 'terminated'
  is_active: boolean
  number_of_users?: number
  daily_cv_processing_limit?: number
  daily_job_offer_limit?: number
  subscription_plan?: string
  subscription_duration_type?: 'days' | 'deadline'
  subscription_duration_days?: number
  subscription_deadline?: string
  ai_provider?: string
  ai_ollama_host?: string
  ai_openrouter_host?: string
  ai_model?: string
  ai_model_ocr?: string
  ai_token?: string
  allow_external_cv_ai?: boolean
  system_email_language?: 'fr' | 'en'
  manual_matching_time_minutes?: number
  created_at: string
  updated_at: string
  created_by?: number
  admin_email?: string
  admin_username?: string
  invitation_email_sent?: boolean
  admin_created?: boolean
  consumption?: {
    candidates_count: number
    candidate_creation_invalid_count?: number
    offers_count: number
    total_offer_cost: number
    total_offer_input_tokens: number
    total_offer_output_tokens: number
    total_candidate_cost: number
    total_candidate_input_tokens: number
    total_candidate_output_tokens: number
    total_candidate_invalid_cost?: number
    total_candidate_invalid_input_tokens?: number
    total_candidate_invalid_output_tokens?: number
    total_cost: number
  }
}

export interface CreateOrganizationData {
  name: string
  description?: string
  domain?: string
  website?: string
  logo?: File | string
  email?: string
  phone?: string
  address?: string
  status?: 'active' | 'suspended' | 'cancelled' | 'payment_failed' | 'terminated'
  is_active?: boolean
  number_of_users?: number
  daily_cv_processing_limit?: number
  daily_job_offer_limit?: number
  subscription_plan?: string
  subscription_duration_type?: 'days' | 'deadline'
  subscription_duration_days?: number
  subscription_deadline?: string
  ai_provider?: string
  ai_ollama_host?: string
  ai_openrouter_host?: string
  ai_model?: string
  ai_model_ocr?: string
  ai_token?: string
  use_default_ai_token?: boolean
  allow_external_cv_ai?: boolean
  system_email_language?: 'fr' | 'en'
  admin_email: string
  admin_setup_mode?: 'invitation' | 'credentials'
  admin_password?: string
  admin_password_confirm?: string
  admin_name?: string
}

export interface OrganizationInvitationDetails {
  email: string
  organization_name: string
  expires_at: string
  status: 'pending' | 'accepted' | 'expired'
}

export interface LoginResponse {
  message: string
  user?: User
  tokens?: {
    refresh: string
    access: string
  }
  requires_2fa?: boolean
  email?: string
  authenticator_available?: boolean
  email_available?: boolean
}

export interface RegisterData {
  email: string
  first_name: string
  last_name: string
  password: string
  confirm_password: string
  is_recruiter: boolean
  accept_terms: boolean
}

// Paginated response interface
export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface CursorPaginatedMessages<T> {
  results: T[]
  has_more: boolean
  next_before: number | null
  context_limit?: ContextLimitState
}

// Candidates API
export const candidatesApi = {
  getAll: (params?: Record<string, any>) => api.get<PaginatedResponse<Candidate>>('/candidates/', { params }),
  getAllIds: (params?: Record<string, any>) => api.get<number[]>('/candidates/all-ids/', { params }),
  getAllPages: (params?: Record<string, any>) => fetchAllPages<Candidate>('/candidates/', params),
  getCandidates: (params?: Record<string, any>) => api.get<PaginatedResponse<Candidate>>('/candidates/', { params }),
  getById: (id: number, config?: AxiosRequestConfig) => api.get<Candidate>(`/candidates/${id}/`, config),
  create: (candidate: Omit<Candidate, 'id'>) => api.post<Candidate>('/candidates/', candidate),
  update: (id: number, candidate: Partial<Candidate>) => api.patch<Candidate>(`/candidates/${id}/`, candidate),
  delete: (id: number) => api.delete(`/candidates/${id}/`),
  bulkDelete: (ids: number[]) => api.post<{ deleted_count: number; message: string }>('/candidates/bulk-delete/', { ids }),
  getStats: (params?: { organization?: number, created_after?: string }) => api.get<{
    total_candidates: number
    hired_candidates: number
    new_candidates_7_days: number
    pending_cvs: number
    category_counts?: Record<string, number>
  }>('/candidates/stats/', { params }),
}

// Offers API
export const offersApi = {
  getAll: (params?: Record<string, any>) => api.get<PaginatedResponse<Offer>>('/offers/', { params }),
  getAllPages: (params?: Record<string, any>) => fetchAllPages<Offer>('/offers/', params),
  getById: (id: number, params?: Record<string, any>) => api.get<Offer>(`/offers/${id}/`, { params }),
  create: (offer: Partial<Offer>) => api.post<Offer>('/offers/', offer),
  update: (id: number, offer: Partial<Offer>) => api.patch<Offer>(`/offers/${id}/`, offer),
  delete: (id: number) => api.delete(`/offers/${id}/`),
  updateStatus: (id: number, status: 'draft' | 'published' | 'closed') => api.patch(`/offers/${id}/status/`, { status }),
  getStats: (params?: { organization?: number, created_after?: string }) => api.get<{ total_offers: number, published_offers: number, draft_offers: number, closed_offers: number, expired_offers: number, conversion_rate: number }>('/offers/stats/', { params }),
  getByCandidate: (candidateId: number) => api.get<Offer[]>(`/offers/candidate/${candidateId}/`),
  search: (query: string) => api.get<{ offers: Offer[], candidates: Candidate[], total_results: number }>(`/offers/global-search/?q=${encodeURIComponent(query)}`),
  getTestData: () => api.get<{
    user_id: number
    total_offers: number
    total_candidates: number
    offers: Array<{ id: number, title: string }>
    candidates: Array<{ id: number, name: string, role: string }>
  }>('/offers/test-data/'),
  importFromDocument: (file: File, platformLanguage?: string, hiringTemplateId?: number | null) => {
    const formData = new FormData()
    formData.append('file', file)
    if (platformLanguage) {
      formData.append('platform_language', platformLanguage)
    }
    if (hiringTemplateId != null) {
      formData.append('hiring_template', String(hiringTemplateId))
    }
    return api.post<{ message: string, offer: Offer, extracted_text?: string, warnings?: string[] }>('/offers/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  importFromURL: (url: string, site?: string, platformLanguage?: string, hiringTemplateId?: number | null) =>
    api.post<{ message: string, offer: Offer, extracted_text?: string, warnings?: string[] }>('/offers/import-url/', {
      url,
      site,
      ...(platformLanguage ? { platform_language: platformLanguage } : {}),
      ...(hiringTemplateId != null ? { hiring_template: hiringTemplateId } : {}),
    }),
  improveDescription: (id: number) =>
    api.post<{ message: string, ai_description: string, ai_generated_at: string }>(`/offers/${id}/improve-description/`),
  applyAiDescription: (id: number) =>
    api.post<{ message: string, offer: Offer }>(`/offers/${id}/apply-ai-description/`),
  clone: (id: number, hiringTemplateId?: number | null) =>
    api.post<{ message: string, offer: Offer }>(`/offers/${id}/clone/`, {
      ...(hiringTemplateId != null ? { hiring_template: hiringTemplateId } : {}),
    }),
  /**
   * Sauvegarde directement en BDD les compétences sélectionnées depuis le popup
   * "Analyse & Recommandations IA", SANS déclencher le LLM.
   * Utilise l'endpoint dédié /update-ai-skills/ au lieu du PATCH générique
   * (qui ré-exécuterait le LLM sur une offre publiée).
   */
  updateAiSkills: (
    id: number,
    payload: {
      requirements?: { hard_skills_mentionnées: string[]; hard_skills_non_mentionnées: string[] }
      soft_skills?:  { soft_skills_mentionnées: string[];  soft_skills_non_mentionnées: string[]  }
      benefits?:     { nice_to_have_mentionnées: string[];  nice_to_have_non_mentionnées: string[]  }
      ai_matching_weights?: Record<string, number>
      skill_weights?: Record<string, number>
      filter_matching?: Offer['filter_matching']
    }
  ) => api.post<Offer>(`/offers/${id}/update-ai-skills/`, payload),
}

export const departmentsApi = {
  list: (params?: { search?: string; limit?: number }) =>
    api.get<Department[]>('/offers/departments/', { params }),
  create: (name: string) =>
    api.post<Department>('/offers/departments/', { name }),
}

// Applications API
export const applicationsApi = {
  getAll: () => api.get<PaginatedResponse<Application>>('/applications/'),
  getById: (id: number) => api.get<Application>(`/applications/${id}/`),
  create: (application: { candidate: number, offer: number, status?: string, current_step?: number | null, match_score?: number, notes?: string, cover_letter?: string }) => api.post<Application>('/applications/create/', application),
  update: (id: number, application: Partial<Application>) => api.patch<Application>(`/applications/${id}/`, application),
  delete: (id: number) => api.delete(`/applications/${id}/`),
  bulkCreate: (data: { offer: number, candidate_ids: number[], notes?: string }) => api.post<{ success: boolean, message: string, created_count: number }>('/applications/bulk-create/', data),
  getByOfferIds: (offerId: number) =>
    api.get<{ ids: number[]; offer_id: number }>(`/applications/offer/${offerId}/ids/`),
  getByOffer: (offerId: number, params?: { 
    summary?: boolean; 
    page?: number; 
    page_size?: number; 
    status?: string; 
    current_step?: number;
    ordering?: string;
    search?: string;
  }) =>
    api.get<{ applications: Application[], total_count?: number; count?: number; page: number; page_size: number; has_more: boolean }>(`/applications/offer/${offerId}/`, { params }),
  getByCandidate: (candidateId: number, params?: { page?: number; page_size?: number }) => api.get<{
    success: boolean,
    candidate: { id: number, name: string },
    applications: Array<Application & {
      offer_id: number,
      offer_title: string,
      current_step_name?: string | null,
      current_step_id?: number | null,
      current_step_status?: string | null,
      process_status?: string | null
    }>,
    total_count: number,
    page?: number,
    page_size?: number,
    has_more?: boolean
  }>(`/applications/candidate/${candidateId}/`, { params }),
  updateStatus: (id: number, status: string, currentStepId?: number | null) =>
    api.put<{ success: boolean, application: Application, message: string }>(`/applications/${id}/status/`, currentStepId !== undefined ? { status, current_step: currentStepId } : { status }),
  getStats: (offerId?: number) => {
    const url = offerId ? `/applications/stats/${offerId}/` : '/applications/stats/'
    return api.get<{
      total_applications: number,
      by_status: Record<string, { count: number, label: string }>,
      recent_applications: number
    }>(url)
  },
  getRecentOffersStats: (params?: { organization?: number, created_after?: string, limit?: number }) =>
    api.get<{ offers: RecentOfferStatsItem[] }>('/applications/recent-offers-stats/', { params }),
  getMatchingStats: (params?: { organization?: number, created_after?: string }) =>
    api.get<{
      total_matching_jobs: number,
      total_processing_time: number,
      total_candidates: number,
      average_analysis_time: number,
      time_saved_minutes: number,
      manual_matching_time_minutes?: number,
      history: Array<{
        date: string,
        total_processing_time: number,
        total_candidates: number,
        time_saved_minutes: number,
      }>,
    }>('/applications/matching-jobs/stats/', { params }),
  getMatchingDashboardSettings: () =>
    api.get<{ manual_matching_time_minutes: number }>('/applications/matching-jobs/dashboard-settings/'),
  updateMatchingDashboardSettings: (data: { manual_matching_time_minutes: number }) =>
    api.patch<{ manual_matching_time_minutes: number }>('/applications/matching-jobs/dashboard-settings/', data),
  getMatchingCategoryAverages: (params?: { organization?: number, created_after?: string, offer?: number }) =>
    api.get<{
      category_averages: {
        languages: number,
        hard_skills: number,
        soft_skills: number,
        nice_to_have: number,
        niveau_etude: number,
        main_missions: number,
        certifications: number,
        experience_years: number,
      },
      total_results: number,
    }>('/applications/matching-jobs/category-averages/', { params }),
  getCandidateScoreDistribution: (params?: { organization?: number, created_after?: string, offer?: number }) =>
    api.get<{
      points: Array<{
        application_id: number | null,
        candidate_id: number,
        candidate_name: string,
        cv_matching_score: number,
        interview_score: number,
      }>,
      total_candidates: number,
    }>('/applications/matching-jobs/candidate-score-distribution/', { params }),
  aiMatching: (data: { candidate_ids: number[], offer_id: number }) =>
    api.post<{
      success: boolean,
      results: MatchingResult[],
      total_candidates: number,
      offer: Offer,
      new_matches?: number,
      message?: string,
    }>('/applications/ai-matching/', data),
}

// Interview Interfaces
export interface Interview {
  interviewer_name?: string | null
  id: number
  candidate: number
  candidate_id: number
  candidate_name: string
  application?: number | null
  application_id?: number | null
  offer: number
  offer_id: number
  title: string
  scheduled_at: string
  duration_minutes: number
  end_time: string
  location?: string | null
  meeting_link?: string | null
  notes?: string | null
  step_order?: number | null
  step_name?: string | null
  created_at: string
  updated_at: string
}

// Interviews API
export const interviewsApi = {
  getAll: () => api.get<Interview[]>('/applications/interviews/'),
  getById: (id: number) => api.get<Interview>(`/applications/interviews/${id}/`),
  create: (interview: {
    candidate: number
    application?: number | null
    offer: number
    title: string
    scheduled_at: string
    duration_minutes: number
    location?: string | null
    meeting_link?: string | null
    notes?: string | null
    interviewer_name?: string | null
    step_order?: number | null
    step_name?: string | null
  }) => api.post<Interview>('/applications/interviews/', interview),
  update: (id: number, interview: Partial<Interview>) => api.patch<Interview>(`/applications/interviews/${id}/`, interview),
  delete: (id: number) => api.delete(`/applications/interviews/${id}/`),
  getByOffer: (offerId: number) => api.get<Interview[]>(`/applications/interviews/offer/${offerId}/`),
}

// Uploads API
export const uploadsApi = {
  getAll: () => api.get<PaginatedResponse<Upload>>('/uploads/'),
  getByCandidateId: (candidateId: number) => api.get<Upload[]>(`/uploads/candidate/${candidateId}/`),
  create: (formData: FormData) => api.post<Upload>('/uploads/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getProcessingStatus: () => api.get<{
    hasActiveProcessing: boolean
    activeProcessing: boolean
    recentUploads: boolean
  }>('/uploads/processing-status/'),
  delete: (id: number) => api.delete(`/uploads/${id}/`),
  reprocess: (id: number) => api.post(`/uploads/${id}/reprocess/`),
  download: (id: number) => `${API_BASE_URL}/uploads/${id}/download/`,
  view: (id: number) => `${API_BASE_URL}/uploads/${id}/view/`,
  checkAiAvailability: () => api.get('/uploads/ai-status/'),
  grantExternalCvAiConsent: () => api.post<{ allow_external_cv_ai: boolean }>('/uploads/external-cv-ai-consent/'),
  // Batch upload functionality
  uploadBatch: (formData: FormData) => api.post<{
    batch_id: string
    batch_name: string
    total_files: number
    status: string
    task_id: string
    message: string
  }>('/uploads/batch/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getBatchStatus: (batchId: string) => api.get<{
    batch_id: string
    batch_name: string
    status: string
    progress_percentage: number
    total_files: number
    processed_files: number
    successful_files: number
    failed_files: number
    created_at: string
    started_at: string
    completed_at: string
    task_id: string
    files: Array<{
      file_index: number
      filename: string
      status: string
      error_message?: string
      cv_upload_id: number
      candidate_id?: number
    }>
    skipped_files?: Array<{
      filename: string
      file_index: number
      reason: string
      message: string
      duplicate_cv_id?: number
      duplicate_candidate_id?: number
      duplicate_candidate_name?: string
      duplicate_uploaded_at?: string
    }>
  }>(`/uploads/batch/${batchId}/status/`),
  listBatches: () => api.get<{
    batches: Array<{
      batch_id: string
      batch_name: string
      status: string
      progress_percentage: number
      total_files: number
      processed_files: number
      successful_files: number
      failed_files: number
      created_at: string
      completed_at: string
      is_completed: boolean
    }>
    total_count: number
  }>('/uploads/batch/list/'),
  cancelBatch: (batchId: string) => api.delete(`/uploads/batch/${batchId}/cancel/`),
  stageBatch: (formData: FormData) => api.post<{
    batch_id: string | null
    batch_name: string
    status: string
    ready_count: number
    duplicate_count: number
    error_count: number
    total_files: number
    message: string
    files: Array<{
      filename: string
      status: 'ready' | 'duplicate' | 'error'
      reason?: string
      message?: string
      file_index?: number
      cv_upload_id?: number
      file_size?: number
      duplicate_candidate_name?: string
    }>
  }>('/uploads/batch/stage/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  startStagedBatch: (batchId: string, teamId?: string | null) => api.post<{
    batch_id: string
    batch_name: string
    total_files: number
    status: string
    task_id: string
    message: string
  }>(`/uploads/batch/${batchId}/start/`, { team_id: teamId || null }),
  pauseBatch: (batchId: string) => api.post(`/uploads/batch/${batchId}/pause/`),
  resumeBatch: (batchId: string) => api.post(`/uploads/batch/${batchId}/resume/`),
  deleteStagedFile: (batchId: string, cvUploadId: number) => api.delete(`/uploads/batch/${batchId}/files/${cvUploadId}/`),
  discardStagedBatch: (batchId: string) => api.delete(`/uploads/batch/${batchId}/discard/`),

  // Gmail integration
  getGmailAuthUrl: () => api.get<{ auth_url: string }>('/uploads/gmail/auth-url/'),
  fetchGmailPdfs: (days: number) => api.get<{ pdfs: Array<any> }>(`/uploads/gmail/fetch-pdfs/?days=${days}&_t=${new Date().getTime()}`),
  importGmailPdfs: (data: { files: {id: string, filename: string, subject?: string, body?: string}[], team_id?: string | null }) => api.post<{ batch_id: string, batch_name: string, total_files: number }>('/uploads/gmail/import/', data),

  // IMAP integration
  connectImapMailbox: (data: { host: string, port: number, username: string, password: string, folder?: string, use_ssl?: boolean }) =>
    api.post<{ connected: boolean, email?: string }>('/uploads/imap/connect/', data),
  connectOutlookMailbox: (data: { username?: string, email?: string, password: string, folder?: string }) =>
    api.post<{ connected: boolean, host: string, email?: string, message?: string }>('/uploads/outlook/connect/', data),
  fetchImapPdfs: (days: number) => api.get<{ pdfs: Array<any> }>(`/uploads/imap/fetch-pdfs/?days=${days}&_t=${new Date().getTime()}`),
  importImapPdfs: (data: { files: {id: string, filename: string, subject?: string, body?: string}[], team_id?: string | null }) =>
    api.post<{ batch_id: string, batch_name: string, total_files: number }>('/uploads/imap/import/', data),

  // AutoSync Logs
  getAutoSyncLogs: (params?: { page?: number; limit?: number }) => api.get<PaginatedResponse<any>>('/uploads/auto-sync-logs/', { params }),
  logAutoSyncAction: (data: {
    action: 'sync_triggered' | 'import_success' | 'import_failed' | 'assign_offer' | 'reject_candidate'
    filename?: string
    sender?: string
    subject?: string
    candidate_id?: number
    offer_id?: number
    status?: 'success' | 'failed' | 'pending'
    details?: string
  }) => api.post('/uploads/auto-sync-logs/', data),
}

// Teams API
export const teamsApi = {
  getAll: () => api.get<PaginatedResponse<Team>>('/teams/'),
  getAllPages: (params?: Record<string, any>) => fetchAllPages<Team>('/teams/', params),
  getById: (id: number) => api.get<Team>(`/teams/${id}/`),
  create: (team: TeamPayload) => api.post<Team>('/teams/', team),
  update: (id: number, team: Partial<TeamPayload>) => api.patch<Team>(`/teams/${id}/`, team),
  delete: (id: number) => api.delete(`/teams/${id}/`),
}

// Jobs API
export const jobsApi = {
  getAll: () => api.get<Job[]>('/jobs'),
  getById: (id: number) => api.get<Job>(`/jobs/${id}`),
  create: (job: Omit<Job, 'id'>) => api.post<Job>('/jobs', job),
  update: (id: number, job: Partial<Job>) => api.patch<Job>(`/jobs/${id}`, job),
  delete: (id: number) => api.delete(`/jobs/${id}`),
}

// Activities API
export const activitiesApi = {
  getAll: () => api.get<Activity[]>('/activities'),
  create: (activity: Omit<Activity, 'id'>) => api.post<Activity>('/activities', activity),
}

// Authentication API
export const authApi = {
  login: (email: string, password: string, otpToken?: string) =>
    api.post<LoginResponse>('/auth/login/', { email, password, otp_token: otpToken }),
  register: (data: RegisterData) =>
    api.post<LoginResponse>('/auth/register/', {
      email: data.email,
      username: (data as any).username ?? data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      password: data.password,
      password_confirm: data.confirm_password,
      is_recruiter: data.is_recruiter,
      accept_terms: data.accept_terms,
    }),
  logout: () => api.post('/auth/logout/'),
  getProfile: () => api.get<User>('/auth/profile/'),
  updateProfile: (data: Partial<User>) =>
    api.put<User>('/auth/profile/update/', data),
  uploadAvatar: (file: File) => {
    const formData = new FormData()
    formData.append('avatar', file)
    return api.post<{ message: string; user: User }>('/auth/profile/avatar/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  deleteAvatar: () =>
    api.delete<{ message: string; user: User }>('/auth/profile/avatar/delete/'),
  // Change password: expects payload { old_password, new_password, new_password_confirm }
  changePassword: (data: { old_password: string; new_password: string; new_password_confirm: string }) =>
    api.post('/auth/change-password/', data),
  // Backwards-compatible alias (object form)
  postChangePassword: (data: { old_password: string; new_password: string; new_password_confirm: string }) =>
    api.post('/auth/change-password/', data),
  // Session timeout endpoints
  getSessionTimeout: () => api.get<{ session_timeout: number }>('/auth/session-timeout/'),
  updateSessionTimeout: (data: { session_timeout: number }) => api.patch('/auth/session-timeout/', data),
  refreshToken: (refreshToken?: string) =>
    api.post<{ message?: string; access?: string }>(
      '/auth/token/refresh/',
      refreshToken ? { refresh: refreshToken } : {}
    ),

  // Password reset
  requestPasswordReset: (email: string, lang?: string) =>
    api.post<{ message: string; error?: string }>('/auth/password-reset/request/', { email, lang }),
  confirmPasswordReset: (payload: { uid: string; token: string; new_password: string }) =>
    api.post<{ message: string; error?: string }>('/auth/password-reset/confirm/', payload),

  // 2FA methods
  toggle2FA: (enable: boolean, enableEmail?: boolean) =>
    api.post<{ message: string; qr?: string; secret?: string; backup_codes?: string[]; authenticator_enabled?: boolean; email_enabled?: boolean }>('/auth/2fa/toggle/', { enable, enable_email: enableEmail || false }),
  get2FAStatus: () =>
    api.get<{ two_factor_enabled: boolean; otp_secret_configured: boolean; email_enabled: boolean }>('/auth/2fa/status/'),
  verifyOTP: (email: string, otpToken: string, method?: 'authenticator' | 'email') =>
    api.post<LoginResponse>('/auth/2fa/verify/', { email, otp_token: otpToken, method: method || 'authenticator' }),
  requestEmailOTP: (email: string) =>
    api.post<{ message: string }>('/auth/2fa/request-email-otp/', { email }),
  verifyBackupCode: (email: string, backupCode: string) =>
    api.post<LoginResponse>('/auth/2fa/verify-backup/', { email, backup_code: backupCode }),
  reset2FA: () =>
    api.post<{ message: string; qr: string; secret: string; backup_codes: string[] }>('/auth/2fa/reset/'),
  getBackupCodes: () =>
    api.get<{ unused_codes_count: number; has_unused_codes: boolean }>('/auth/2fa/backup-codes/'),
  request2FARecovery: (email: string) =>
    api.post<{ message: string }>('/auth/2fa/recovery/request/', { email }),
  checkRecoveryToken: (token: string, email: string) =>
    api.get<{ valid: boolean }>('/auth/2fa/recovery/status/', { params: { token, email } }),
  verifyRecoveryToken: (token: string, email: string) =>
    api.post<{ message: string }>('/auth/2fa/recovery/verify/', { token, email }),

}

// Preferences API
export const preferencesApi = {
  getPreferences: () => api.get<NotificationPreferences>('/auth/preferences/'),
  updatePreferences: (data: Partial<NotificationPreferences>) => api.patch<NotificationPreferences>('/auth/preferences/', data),
}

// Matching Jobs API
export const matchingJobsApi = {
  getAll: (page?: number, filters?: { offer?: number; status?: string }) => {
    const params = new URLSearchParams()
    if (page) params.append('page', page.toString())
    if (filters?.offer) params.append('offer', filters.offer.toString())
    if (filters?.status) params.append('status', filters.status)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return api.get<PaginatedResponse<MatchingJob>>(`/applications/matching-jobs/${qs}`)
  },
  getById: (id: number) => api.get<MatchingJob>(`/applications/matching-jobs/${id}/`),
  getStatus: (id: number) => api.get<{ success: boolean, job: MatchingJob }>(`/applications/matching-jobs/${id}/status/`),
  startBackgroundMatching: (data: {
    candidate_ids?: number[]
    team_ids?: number[]
    offer_id: number
    name?: string
  }) => api.post<{ success: boolean, job: MatchingJob, task_id: string, message: string }>('/applications/start-matching/', data),
  create: (data: {
    name: string
    offer: number
    candidate_ids: number[]
    team_ids?: number[]
  }) => api.post<MatchingJob>('/applications/matching-jobs/', data),
}

// Roles and Permissions API
export const rolesApi = {
  // Roles
  getRoles: () => api.get<Role[] | { results: Role[] }>('/roles/roles/'),
  getRole: (id: number) => api.get<Role>(`/roles/roles/${id}/`),
  createRole: (data: CreateRoleData) => api.post<Role>('/roles/roles/', data),
  updateRole: (id: number, data: UpdateRoleData) => api.put<Role>(`/roles/roles/${id}/`, data),
  deleteRole: (id: number) => api.delete(`/roles/roles/${id}/`),

  // Permissions
  getPermissions: (category?: string, page?: number) => {
    const queryParams: string[] = []
    if (category) queryParams.push(`category=${encodeURIComponent(category)}`)
    if (page) queryParams.push(`page=${page}`)
    const qs = queryParams.length ? `?${queryParams.join('&')}` : ''
    return api.get<Permission[] | { results: Permission[]; next?: string }>(`/roles/permissions/${qs}`)
  },

  // User Roles
  getUserRoles: (userId?: number) => {
    const params = userId ? `?user_id=${userId}` : ''
    return api.get<UserRole[]>(`/roles/user-roles/${params}`)
  },
  assignRolesToUser: (data: { user_id: number; role_ids: number[] }) =>
    api.post<{ message: string }>('/roles/assign-roles/', data),
  removeRoleFromUser: (userId: number, roleId: number) =>
    api.delete(`/roles/users/${userId}/roles/${roleId}/`),

  // Permission Checks
  getUserPermissions: () => api.get<{ permissions: UserPermission[] }>('/roles/user-permissions/'),
  checkPermission: (codename: string) =>
    api.get<{ has_permission: boolean; roles: string[] }>(`/roles/check-permission/${codename}/`),

  // Users with Roles
  getUsersWithRoles: () => api.get<UserWithRoles[]>('/roles/users/'),

  // Logs
  getPermissionLogs: (params?: { user_id?: number; action?: string; success?: boolean }) =>
    api.get<PermissionLog[]>('/roles/logs/', { params }),
}

// Role and Permission Types
export interface Permission {
  id: number
  name: string
  codename: string
  description: string
  category: string
  created_at: string
  updated_at: string
}

export interface Role {
  id: number
  name: string
  description: string
  color: string
  is_active: boolean
  is_system_role: boolean
  role_type?: 'system' | 'organization'
  permission_count: number
  permissions: Permission[]
  created_at: string
  updated_at: string
  created_by?: number
}

export interface UserRole {
  id: number
  user: number
  role: number
  role_name: string
  role_color: string
  role_type?: string
  user_email: string
  organization?: number | Organization | null
  organization_name?: string
  assigned_at: string
  assigned_by?: number
}

export interface UserPermission {
  codename: string
  name: string
  category: string
  role: string
}

export interface UserWithRoles {
  id: number
  email: string
  full_name: string
  username?: string
  first_name?: string
  last_name?: string
  company?: string
  phone?: string
  roles: UserRole[]
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
  date_joined?: string
  avatar_url?: string
  organization?: number | Organization | null
}

export interface PermissionLog {
  id: number
  user: number
  user_email: string
  action: string
  resource: string
  permission?: number
  permission_name?: string
  role?: number
  role_name?: string
  success: boolean
  ip_address?: string
  user_agent: string
  timestamp: string
}

export interface CreateRoleData {
  name: string
  description?: string
  color?: string
  is_active?: boolean
  permission_ids?: number[]
}

export interface UpdateRoleData {
  name?: string
  description?: string
  color?: string
  is_active?: boolean
  permission_ids?: number[]
}

// User Management API
export const userManagementApi = {
  // Users
  getUsers: () => api.get<UserWithRoles[] | { results: UserWithRoles[] }>('/roles/users/'),
  getUser: (id: number) => api.get<User>(`/auth/profile/${id}/`),
  createUser: (data: CreateUserData) => api.post<User>('/auth/register/', {
    email: data.email,
    username: data.username ?? data.email,
    first_name: data.first_name,
    last_name: data.last_name,
    // Backend requires password_confirm to match password
    password: data.password,
    password_confirm: data.password_confirm ?? data.password,
    // Optional fields supported by backend serializer
    // company and phone are allowed; only include if present
    ...(data as any).company ? { company: (data as any).company } : {},
    ...(data as any).phone ? { phone: (data as any).phone } : {},
  }),
  // Use PATCH for partial updates so we don't have to send all required fields
  // (e.g. when only toggling is_active from the UserManagement table).
  updateUser: (id: number, data: UpdateUserData) => api.patch<User>(`/auth/profile/${id}/`, data),
  deleteUser: (id: number) => api.delete(`/auth/profile/${id}/`),

  // User Roles
  getUserRoles: (userId: number) => api.get<UserRole[]>(`/roles/user-roles/?user_id=${userId}`),
  assignRolesToUser: (data: { user_id: number; role_ids: number[] }) =>
    api.post<{ message: string }>('/roles/assign-roles/', data),
  removeRoleFromUser: (userId: number, roleId: number) =>
    api.delete(`/roles/users/${userId}/roles/${roleId}/`),
}

// User Management Types
export interface CreateUserData {
  email: string
  username?: string
  password: string
  password_confirm?: string
  first_name?: string
  last_name?: string
  full_name?: string
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
}

export interface UpdateUserData {
  email?: string
  username?: string
  first_name?: string
  last_name?: string
  full_name?: string
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
  avatar?: File
}

// Hiring Process API
export const hiringProcessApi = {
  // Templates
  getTemplates: () => api.get<HiringProcessTemplate[]>('/hiring-process/templates/'),
  getTemplate: (id: number) => api.get<HiringProcessTemplate>(`/hiring-process/templates/${id}/`),
  createTemplate: (data: CreateHiringProcessTemplateData) => api.post<HiringProcessTemplate>('/hiring-process/templates/', data),
  updateTemplate: (id: number, data: UpdateHiringProcessTemplateData) => api.put<HiringProcessTemplate>(`/hiring-process/templates/${id}/`, data),
  deleteTemplate: (id: number) => api.delete(`/hiring-process/templates/${id}/`),
  duplicateTemplate: (id: number) => api.post<HiringProcessTemplate>(`/hiring-process/templates/${id}/duplicate/`),
  getDefaultTemplate: () => api.get<HiringProcessTemplate>('/hiring-process/templates/default/'),

  // Steps
  getSteps: (templateId?: number) => {
    const params = templateId ? `?template_id=${templateId}` : ''
    return api.get<HiringStep[]>(`/hiring-process/steps/${params}`)
  },
  /** Steps for an offer: default template steps + offer-specific steps (for offer detail kanban) */
  getStepsForOffer: (offerId: number) =>
    api.get<HiringStep[] | PaginatedResponse<HiringStep>>(`/hiring-process/steps/?offer_id=${offerId}`),
  getStep: (id: number) => api.get<HiringStep>(`/hiring-process/steps/${id}/`),
  createStep: (data: CreateHiringStepData) => api.post<HiringStep>('/hiring-process/steps/', data),
  updateStep: (id: number, data: UpdateHiringStepData) => api.put<HiringStep>(`/hiring-process/steps/${id}/`, data),
  deleteStep: (id: number) => api.delete(`/hiring-process/steps/${id}/`),
  reorderStep: (id: number, order: number) => api.post(`/hiring-process/steps/${id}/reorder/`, { order }),

  // Actions
  getActions: (stepId?: number) => {
    if (stepId) {
      return api.get<StepAction[]>(`/hiring-process/actions/?step_id=${stepId}`)
    }
    return api.get<StepAction[]>(`/hiring-process/actions/`)
  },
  getAction: (id: number) => api.get<StepAction>(`/hiring-process/actions/${id}/`),
  createAction: (data: CreateStepActionData) => api.post<StepAction>('/hiring-process/actions/', data),
  updateAction: (id: number, data: UpdateStepActionData) => api.put<StepAction>(`/hiring-process/actions/${id}/`, data),
  deleteAction: (id: number) => api.delete(`/hiring-process/actions/${id}/`),

  // Candidate Processes
  getCandidateProcesses: (candidateId?: number, templateId?: number) => {
    const params = new URLSearchParams()
    if (candidateId) params.append('candidate_id', candidateId.toString())
    if (templateId) params.append('template_id', templateId.toString())
    return api.get<CandidateProcess[]>(`/hiring-process/candidate-processes/?${params.toString()}`)
  },
  getCandidateProcess: (id: number) => api.get<CandidateProcess>(`/hiring-process/candidate-processes/${id}/`),
  createCandidateProcess: (data: CreateCandidateProcessData) => api.post<CandidateProcess>('/hiring-process/candidate-processes/', data),
  updateCandidateProcess: (id: number, data: UpdateCandidateProcessData) => api.put<CandidateProcess>(`/hiring-process/candidate-processes/${id}/`, data),
  deleteCandidateProcess: (id: number) => api.delete(`/hiring-process/candidate-processes/${id}/`),
  moveCandidateToStep: (id: number, stepId: number) => api.post(`/hiring-process/candidate-processes/${id}/move-to-step/`, { step_id: stepId }),
  pauseCandidateProcess: (id: number) => api.post(`/hiring-process/candidate-processes/${id}/pause/`),
  resumeCandidateProcess: (id: number) => api.post(`/hiring-process/candidate-processes/${id}/resume/`),

  // Step Progress
  getStepProgress: (candidateProcessId?: number) => {
    const params = candidateProcessId ? `?candidate_process_id=${candidateProcessId}` : ''
    return api.get<StepProgress[]>(`/hiring-process/step-progress/${params}`)
  },
  getStepProgressItem: (id: number) => api.get<StepProgress>(`/hiring-process/step-progress/${id}/`),
  updateStepProgress: (id: number, data: UpdateStepProgressData) => api.put<StepProgress>(`/hiring-process/step-progress/${id}/`, data),
  completeStep: (id: number) => api.post(`/hiring-process/step-progress/${id}/complete/`),
  skipStep: (id: number) => api.post(`/hiring-process/step-progress/${id}/skip/`),
  previewEmail: (id: number, data: { action_id: number; recipients?: string[] }) =>
    api.post(`/hiring-process/step-progress/${id}/preview_email/`, data),
  sendManualEmail: (id: number, data: { action_id: number; recipients: string[] }) =>
    api.post(`/hiring-process/step-progress/${id}/send_manual_email/`, data),

  // Action Executions
  getActionExecutions: (stepProgressId?: number) => {
    const params = stepProgressId ? `?step_progress_id=${stepProgressId}` : ''
    return api.get<ActionExecution[]>(`/hiring-process/action-executions/${params}`)
  },
  getActionExecution: (id: number) => api.get<ActionExecution>(`/hiring-process/action-executions/${id}/`),
  executeAction: (id: number) => api.post(`/hiring-process/action-executions/${id}/execute/`),
}

// Hiring Process Types
export interface HiringProcessTemplate {
  id: number
  name: string
  description: string
  color: string
  is_active: boolean
  is_default: boolean
  step_count: number
  steps: HiringStep[]
  created_at: string
  updated_at: string
  created_by?: number
  created_by_name?: string
}

export interface HiringStep {
  id: number
  template: number
  template_name?: string
  offer?: number | null
  name: string
  description: string
  step_type: string
  order: number
  color: string
  is_required: boolean
  estimated_duration: number
  is_active: boolean
  action_count: number
  actions: StepAction[]
  created_at: string
  updated_at: string
  created_by?: number
  is_mandatory?: boolean
  is_deletable?: boolean
  is_system_default?: boolean
}

export interface StepAction {
  id: number
  step: number
  step_name?: string
  name: string
  description: string
  action_type: string
  trigger_type: string
  order: number
  is_active: boolean
  is_required: boolean
  configuration: Record<string, any>
  delay_hours: number
  expires_hours?: number
  created_at: string
  updated_at: string
  created_by?: number
}

export interface CandidateProcess {
  id: number
  candidate: Candidate
  template: number
  template_name: string
  current_step?: number
  current_step_name?: string
  current_step_color?: string
  status: string
  started_at: string
  completed_at?: string
  notes: string
  created_by?: number
  created_by_name?: string
  step_progress: StepProgress[]
  progress_percentage: number
}

export interface StepProgress {
  id: number
  candidate_process: number
  candidate_name?: string
  step: number
  step_name: string
  step_type: string
  step_color: string
  status: string
  started_at: string
  completed_at?: string
  score?: number
  feedback: string
  notes: string
  assigned_to?: number
  assigned_to_name?: string
  created_by?: number
  action_executions: ActionExecution[]
}

export interface ActionExecution {
  id: number
  step_progress: number
  candidate_name?: string
  action: number
  action_name: string
  action_type: string
  status: string
  scheduled_for?: string
  started_at?: string
  completed_at?: string
  result_data: Record<string, any>
  error_message: string
  executed_by?: number
  executed_by_name?: string
}

export interface CreateHiringProcessTemplateData {
  name: string
  description?: string
  color?: string
  is_active?: boolean
  is_default?: boolean
  steps?: CreateHiringStepData[]
}

export interface UpdateHiringProcessTemplateData {
  name?: string
  description?: string
  color?: string
  is_active?: boolean
  is_default?: boolean
  steps?: CreateHiringStepData[]
}

export interface CreateHiringStepData {
  template?: number
  offer?: number | null // When set, step is only for this offer
  /** When creating from an offer kanban: propagate only to this offer (faster). */
  source_offer_id?: number
  name: string
  description?: string
  step_type?: string
  order?: number // Optional - backend will auto-calculate if not provided
  color?: string
  is_required?: boolean
  estimated_duration?: number
  is_active?: boolean
  actions?: CreateStepActionData[]
}

export interface UpdateHiringStepData {
  name?: string
  description?: string
  step_type?: string
  order?: number
  color?: string
  is_required?: boolean
  estimated_duration?: number
  is_active?: boolean
}

export interface CreateStepActionData {
  step: number
  name: string
  description?: string
  action_type: string
  trigger_type?: string
  order?: number // Optional - backend will auto-calculate if not provided
  is_active?: boolean
  is_required?: boolean
  configuration?: Record<string, any>
  delay_hours?: number
  expires_hours?: number | null
}

export interface UpdateStepActionData {
  name?: string
  description?: string
  action_type?: string
  trigger_type?: string
  order?: number
  is_active?: boolean
  is_required?: boolean
  configuration?: Record<string, any>
  delay_hours?: number
  expires_hours?: number
}

export interface CreateCandidateProcessData {
  candidate: number
  template: number
  notes?: string
}

export interface UpdateCandidateProcessData {
  current_step?: number
  status?: string
  notes?: string
}

export interface UpdateStepProgressData {
  status?: string
  score?: number
  feedback?: string
  notes?: string
  assigned_to?: number
}

// Email Configuration API
export const emailConfigApi = {
  // Email Configuration
  getConfig: () => api.get<PaginatedResponse<EmailConfiguration>>('/email/config/'),
  createConfig: (config: Omit<EmailConfiguration, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<EmailConfiguration>('/email/config/', config),
  updateConfig: (id: number, config: Partial<EmailConfiguration>) =>
    api.patch<EmailConfiguration>(`/email/config/${id}/`, config),

  // Email Templates
  getTemplates: (params?: {
    language?: 'fr' | 'en'
    page?: number
    page_size?: number
    search?: string
  }) => api.get<PaginatedResponse<EmailTemplate>>('/email/templates/', { params }),
  getAllTemplates: (params?: { language?: 'fr' | 'en' }) =>
    fetchAllPages<EmailTemplate>('/email/templates/', params),
  getTemplate: (id: number) => api.get<EmailTemplate>(`/email/templates/${id}/`),
  createTemplate: (template: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<EmailTemplate>('/email/templates/', template),
  updateTemplate: (id: number, template: Partial<EmailTemplate>) =>
    api.patch<EmailTemplate>(`/email/templates/${id}/`, template),
  deleteTemplate: (id: number) =>
    api.delete(`/email/templates/${id}/`),

  // Email Logs
  getLogs: (params?: { page?: number; page_size?: number; status?: string; source?: string; search?: string; candidate_id?: number; recipient_type?: 'candidate' | 'system' }) =>
    api.get<PaginatedResponse<EmailLog>>('/email/logs/', { params }),
  getCandidateEmails: (candidateId: number, params?: { page?: number; page_size?: number }) =>
    api.get<PaginatedResponse<EmailLog>>('/email/logs/', { params: { candidate_id: candidateId, ...params } }),

  // Email Actions
  testConfig: (data: EmailTestData) =>
    api.post<{ message: string, status: 'success' | 'error', error_details?: string, error?: string }>('/email/test/', data),
  sendCandidateEmail: (data: { candidate_id: number, template_id?: number, subject?: string, body?: string }) =>
    api.post<{ success: boolean, message: string, to: string, subject: string }>('/email/send-candidate/', data),
  sendBulkCandidateEmails: (data: {
    candidate_ids: number[],
    template_id?: number,
    subject?: string,
    body?: string,
    action_name?: string,
    delay_between_emails?: number
  }) =>
    api.post<{ success: boolean, message: string, task_id: string, total_candidates: number }>('/email/send-bulk/', data),
  getBulkEmailTaskStatus: (taskId: string) =>
    api.get<{
      status: 'processing' | 'completed' | 'failed' | 'pending',
      task_id: string,
      result?: { total: number, success: number, failed: number, errors: string[] },
      error?: string,
      message?: string
    }>(`/email/bulk-task-status/${taskId}/`),
  getStats: () => api.get<EmailStats>('/email/stats/'),
}

// AI Agent Interfaces
export interface ChatSession {
  id: number
  title: string
  language: string
  created_at: string
  updated_at: string
  is_active: boolean
  messages?: ChatMessage[]
  message_count?: number
  continued_from_session_id?: number | null
  continued_to_session_id?: number | null
  handoff_version?: number
  context_limit?: ContextLimitState
}

export interface ContextLimitState {
  level: 'safe' | 'warning' | 'critical' | 'hard'
  usage: number
  usage_percent: number
  warning_threshold: number
  critical_threshold: number
  can_continue_here: boolean
}

export type ContextTimelineStatus = 'Included' | 'Summarized' | 'Excluded'

export interface ContextDiagnostics {
  enabled: true
  captured_at: string
  metrics: Record<string, any>
  context_state: ContextLimitState
  assembled_context: {
    sections: Record<string, {
      label: string
      messages: Array<{ role: string; content: string }>
      token_estimate: number
      sources?: Array<Record<string, any>>
      details?: Array<Record<string, any>>
    }>
    final_messages: Array<{ role: string; content: string }>
    estimated_tokens: number
  }
  timeline: Array<{
    message_id: number
    role: 'user' | 'ai'
    timestamp: string
      status: ContextTimelineStatus
      exclusion_reason?: string | null
      stage?: 'History filtering' | 'Summary compaction' | 'Final token fitting' | 'Current request'
      estimated_tokens: number
      content?: string
      content_preview?: string
      explanation?: string
      effect?: string
      rule_checks?: string[]
  }>
  summary_metadata: Record<string, any>
  call?: Record<string, any>
  compaction?: Record<string, any>
  grounding?: {
    candidates?: ContextGroundingRecord[]
    offers?: ContextGroundingRecord[]
  }
  session_peak?: { budget_usage: number; usage_percent: number; state: string }
  current_request?: { budget_usage: number; usage_percent: number; state: string }
  context_management?: {
    pre_fit_message_count: number
    final_message_count: number
    pre_fit_token_estimate: number
    final_token_estimate: number
    counts: Record<string, number>
    decisions: Array<Record<string, any>>
      duplicate_candidates: Array<Record<string, any>>
      duplicate_policy: string
      count_scope?: string
      filtering_summary?: {
        history_loaded_from_db: number
        included_raw: number
        summarized: number
        excluded: number
        final_fitting_dropped: number
        exclusion_reasons: Record<string, number>
        stage_counts: Record<string, number>
      }
  }
}

export interface ContextGroundingRecord {
  id: number
  name?: string | null
  requested: boolean
  authorized: boolean
  fetched: boolean
  model: string
  updated_at?: string | null
  available_fields?: string[]
  task_whitelist?: string[]
  fields_sent?: string[]
  omitted_fields?: Array<{ field: string; reason: string }>
  token_estimate?: number
}

export interface ContextCallTrace {
  call_id: string
  parent_action_id: string
  agent_name: string
  call_type: string
  attempt_number: number
  provider: string
  model: string
  input_token_estimate: number
  output_reservation: number
  duration_ms: number
  status: string
  provider_request_id?: string | null
  error_type?: string | null
  fitted_message_count: number
  provider_payload_parameters?: Record<string, any>
  trace_content_truncated?: boolean
  trace_archived?: boolean
  provider_response?: string
  provider_response_preview?: string
  provider_response_chars?: number
  usage?: Record<string, any>
  diagnostics: ContextDiagnostics
}

export interface ContextActionTrace {
  action_id: string
  user_message_id?: number | null
  started_at: string
  updated_at?: string
  trace_status?: 'captured' | 'archived' | 'unavailable'
  action_kind?: string
  field_edit?: {
    source?: string
    field?: string
    instruction?: string
    previous_value?: unknown
    generated_value?: unknown
    offer_id?: number | string | null
    source_message_id?: number | string | null
  }
  user_message?: Record<string, any>
  assistant_messages?: Array<Record<string, any>>
  next_form_submission?: Record<string, any>
  calls: ContextCallTrace[]
}

export interface ContextDiagnosticsResponse {
  enabled: true
  diagnostics: ContextDiagnostics | null
  actions?: ContextActionTrace[]
}

export interface SessionContinuationResponse {
  new_session_id: number
  source_session_id: number
  created: boolean
  transferred_summary: Record<string, any>
  restored_candidate_ids: number[]
  restored_offer_ids: number[]
  restored_workflow: { type?: string, status?: string }
  context_usage_metrics: Record<string, any>
  context_limit: ContextLimitState
}

export interface ChatMessage {
  id: number
  message_type: 'user' | 'ai'
  content: string
  timestamp: string
  metadata?: any
}

/** Client-owned commands accepted when creating a message. */
export interface ClientMessageMetadata {
  candidate_id?: number
  candidate_ids?: number[]
  offer_id?: number
  offer_ids?: number[]
  selected_fields?: string[]
  quick_action_id?: string
  intent_hint?: string
  ui_event?: {
    type: 'quick_action' | 'regeneration_command' | 'offer_wizard' | 'form_submission'
    action:
      | 'offer.edit_content'
      | 'offer.regenerate'
      | 'offer.publish'
      | 'offer.archive'
      | 'offer.delete'
      | 'offer.create'
    offer_id?: number
    regeneration_command?: 'select_mode' | 'retry_analysis' | 'regenerate_again' | 'refine' | 'apply_draft'
    regeneration_mode?: string
    draft_values?: Record<string, unknown>
  }
  form_for?: number
  ui_form_submission?: { form_id: string; answers: Record<string, unknown> }
  selection_for?: number
  friendly_message?: string
  ui_form_display?: {
    title: string
    answers: Array<{ field: string; label: string; value: string }>
  }
  retry_of_message_id?: number
  retry_of_generation_id?: string
  retry_attempt?: number
  regeneration_for?: number
  regeneration_command?: 'select_mode' | 'retry_analysis' | 'regenerate_again' | 'refine' | 'apply_draft'
  regeneration_mode?: string
  draft_values?: Record<string, unknown>
  resume_draft?: Record<string, unknown>
  resume_draft_message_id?: number | string
  edit_of_message_id?: number
}

export interface AIAnalysis {
  analysis: string
  status: 'success' | 'error'
  error?: string
}

export interface JobDescription {
  job_description: string
  status: 'success' | 'error'
  error?: string
}

export interface InterviewQuestions {
  questions: string
  status: 'success' | 'error'
  error?: string
}

export interface QuickChatResponse {
  response: string
  status: 'success' | 'error'
}

export interface AIModels {
  models: string[]
  current_model: string
}

export interface AIStatus {
  status: 'available' | 'unavailable'
  model: string
  message: string
  error?: string
}

// AI Agent API
export const aiAssistantApi = {
  // Chat Sessions
  getSessions: () => api.get<PaginatedResponse<ChatSession>>('/ai-agent/sessions/', { timeout: AI_API_TIMEOUTS.sessionList }),
  createSession: (title?: string, language?: string) => api.post<ChatSession>('/ai-agent/sessions/', { title, language }),
  getSession: (id: number) => api.get<ChatSession>(`/ai-agent/sessions/${id}/`),
  updateSession: (id: number, data: Partial<ChatSession>) =>
    api.patch<ChatSession>(`/ai-agent/sessions/${id}/`, data),
  deleteSession: (id: number) => api.delete(`/ai-agent/sessions/${id}/`),
  continueSession: (id: number) =>
    api.post<SessionContinuationResponse>(`/ai-agent/sessions/${id}/continue/`, {}),
  getContextDiagnostics: (id: number) =>
    api.get<ContextDiagnosticsResponse>(`/ai-agent/sessions/${id}/context-diagnostics/`),

  // Chat Messages
  getMessages: (sessionId: number, params?: Record<string, any>, signal?: AbortSignal) =>
    api.get<CursorPaginatedMessages<ChatMessage>>(`/ai-agent/sessions/${sessionId}/messages/`, {
      params,
      timeout: AI_API_TIMEOUTS.sessionMessages,
      signal,
    }),
  sendMessage: (sessionId: number, content: string, metadata?: ClientMessageMetadata) =>
    api.post<ChatMessage>(
      `/ai-agent/sessions/${sessionId}/messages/`,
      { content, metadata },
      { timeout: AI_API_TIMEOUTS.generationPost },
    ),
  registerMessageVersion: (sessionId: number, messageId: number) =>
    api.post(`/ai-agent/sessions/${sessionId}/messages/${messageId}/register-version/`),

  // AI Functions
  analyzeCV: (cvContent: string, jobRequirements?: string) =>
    api.post<AIAnalysis>('/ai-agent/analyze-cv/', { cv_content: cvContent, job_requirements: jobRequirements }),

  createJobDescription: (jobDetails: any) =>
    api.post<JobDescription>('/ai-agent/create-job-description/', { job_details: jobDetails }),

  generateInterviewQuestions: (jobTitle: string, jobRequirements?: string) =>
    api.post<InterviewQuestions>('/ai-agent/generate-interview-questions/', {
      job_title: jobTitle,
      job_requirements: jobRequirements
    }),

  quickChat: (message: string, candidateId?: number) => {
    const payload: any = { message }
    if (candidateId) {
      payload.candidate_id = candidateId
    }
    return api.post<QuickChatResponse>('/ai-agent/quick-chat/', payload)
  },

  // Get user's candidates (paginated)
  getCandidates: (params?: { page?: number; page_size?: number; search?: string; team?: string }) =>
    api.get<{
      candidates: Array<{ id: number; name: string; email?: string; role?: string; profile_picture?: string }>
      count: number
      next: boolean
      page: number
      page_size: number
    }>('/ai-agent/candidates/', { params }),

  // Get user's offers (paginated, lightweight summary for AI chat selector)
  getOffers: (params?: { page?: number; page_size?: number; search?: string; status?: string }) =>
    api.get<{
      offers: Array<{ id: number; job_title: string; department?: string; contract_type?: string; experience_level?: string; status: string; allowed_actions: string[] }>
      count: number
      next: boolean
      page: number
      page_size: number
    }>('/ai-agent/offers/', { params, timeout: AI_API_TIMEOUTS.offerHydration }),

  // System
  getModels: () => api.get<AIModels>('/ai-agent/models/'),
  getStatus: () => api.get<AIStatus>('/ai-agent/status/'),
  // Config
  getConfig: () => api.get<{ id: number; provider: string; ollama_host: string; model: string; model_ocr: string; token?: string; updated_at: string }>(
    '/ai-agent/config/'
  ),
  updateConfig: (data: { provider?: string; ollama_host?: string; model?: string; model_ocr?: string; token?: string }) =>
    api.put<{ id: number; provider: string; ollama_host: string; model: string; model_ocr: string; token?: string; updated_at: string }>(
      '/ai-agent/config/',
      data
    ),
  testConfig: (data: { provider: string; ollama_host: string; model: string; model_ocr?: string; token?: string }) =>
    api.post<{ status: string; model: string; message: string }>(
      '/ai-agent/test-config/',
      data
    ),

  // Create and validate an offer from chat conversation data
  createOfferFromChat: (
    offerData: Record<string, any>,
    validateWithAI: boolean = true,
    sessionId?: number,
    sourceMessageId?: string
  ) =>
    api.post<{ message: string; offer_id: number; job_title: string; status: string; assistant_message_id?: number | null }>(
      '/ai-agent/create-offer-from-chat/',
      {
        offer_data: offerData,
        validate_with_ai: validateWithAI,
        ...(sessionId ? { session_id: sessionId } : {}),
        ...(sourceMessageId ? { source_message_id: sourceMessageId } : {}),
      }
    ),

  cancelOfferProposal: (sessionId: number, sourceMessageId: string) =>
    api.post<{ message: string }>('/ai-agent/cancel-offer-proposal/', {
      session_id: sessionId,
      source_message_id: sourceMessageId,
    }),
  refineOfferProposalField: (
    sessionId: number,
    sourceMessageId: string,
    offerData: Record<string, any>,
    field: string,
    instruction: string,
  ) => api.post<{ field: string; value: any }>('/ai-agent/refine-offer-proposal-field/', {
    session_id: sessionId,
    source_message_id: sourceMessageId,
    offer_data: offerData,
    field,
    instruction,
  }, { timeout: AI_API_TIMEOUTS.generationPost }),
  updateOfferProposal: (
    sessionId: number,
    sourceMessageId: string,
    offerData: Record<string, any>,
  ) => api.post<{ offer_data: Record<string, any> }>('/ai-agent/update-offer-proposal/', {
    session_id: sessionId,
    source_message_id: sourceMessageId,
    offer_data: offerData,
  }),
  prepareManualOfferUpdate: (
    sessionId: number,
    offerId: number,
    offerData: Record<string, any>,
    sourceMessageId?: string,
  ) => api.post<{
    pending_action_id: string
    assistant_message_id: number | null
    preview: Record<string, any>
    offer_id?: number
    offer_url?: string
  }>(
    '/ai-agent/prepare-manual-offer-update/',
    {
      session_id: sessionId,
      offer_id: offerId,
      offer_data: offerData,
      ...(sourceMessageId ? { source_message_id: sourceMessageId } : {}),
    },
  ),
  applyRegenerationDraft: (
    sessionId: number,
    offerId: number,
    draftValues: Record<string, any>,
    sourceMessageId: string,
  ) => api.post<{
    offer_id: number
    offer_url?: string
    created_new_offer?: boolean
    source_offer_id?: number
    source_message_id: number
    pending_action_id: string
    message?: string
  }>('/ai-agent/apply-regeneration-draft/', {
    session_id: sessionId,
    offer_id: offerId,
    draft_values: draftValues,
    source_message_id: sourceMessageId,
  }),
  refineOfferField: (
    sessionId: number,
    offerId: number,
    offerData: Record<string, any>,
    field: string,
    instruction: string,
  ) => api.post<{ field: string; value: any }>('/ai-agent/refine-offer-field/', {
    session_id: sessionId,
    offer_id: offerId,
    offer_data: offerData,
    field,
    instruction,
  }, { timeout: AI_API_TIMEOUTS.generationPost }),
  confirmOfferAction: (pendingActionId: string, sessionId: number) =>
    api.post<any>(`/ai-agent/offer-actions/${pendingActionId}/confirm/`, { session_id: sessionId }),
  cancelOfferAction: (pendingActionId: string, sessionId: number) =>
    api.post<any>(`/ai-agent/offer-actions/${pendingActionId}/cancel/`, { session_id: sessionId }),
}


// Global Search API
export const searchApi = {
  globalSearch: (query: string) => api.get<{
    offers: Offer[]
    candidates: Candidate[]
    total_results: number
  }>(`/offers/global-search/?q=${encodeURIComponent(query)}`),
  testData: () => api.get<{
    user_id: number
    total_offers: number
    total_candidates: number
    offers: Array<{ id: number, title: string }>
    candidates: Array<{ id: number, name: string, role: string }>
  }>('/offers/test-data/'),
}

// Notifications API
export interface Notification {
  id: number
  type: 'system' | 'alert' | 'update' | 'reminder' | 'error' | 'success' | 'info'
  title: string
  message: string
  title_key?: string
  message_key?: string
  message_params?: Record<string, any>
  category?: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  read: boolean
  read_at?: string
  created_at: string
  expires_at?: string
  action_url?: string
  action_type?: string
  action_label?: string
  metadata?: Record<string, any>
  related_object_type?: string
  related_object_id?: number
}

export interface NotificationPreference {
  id: number
  email_enabled: boolean
  sms_enabled: boolean
  push_enabled: boolean
  browser_notifications_enabled: boolean
  quiet_hours_enabled: boolean
  quiet_hours_start?: string
  quiet_hours_end?: string
  category_preferences: Record<string, Record<string, boolean>>
  sound_enabled: boolean
  sound_volume: number
  created_at: string
  updated_at: string
}

export const notificationsApi = {
  // List notifications with filters
  list: (params?: {
    read?: boolean
    category?: string
    type?: string
    priority?: string
    page?: number
    page_size?: number
  }) => api.get<PaginatedResponse<Notification>>('/notifications/notifications/', { params }),

  // Get single notification
  get: (id: number) => api.get<Notification>(`/notifications/notifications/${id}/`),

  // Create notification (admin/system use)
  create: (data: Omit<Notification, 'id' | 'read' | 'read_at' | 'created_at'>) =>
    api.post<Notification>('/notifications/notifications/', data),

  // Update notification
  update: (id: number, data: Partial<Notification>) =>
    api.patch<Notification>(`/notifications/notifications/${id}/`, data),

  // Delete notification
  delete: (id: number) => api.delete(`/notifications/notifications/${id}/`),

  // Get unread count
  getUnreadCount: () => api.get<{ count: number }>('/notifications/notifications/unread_count/'),

  // Mark as read
  markAsRead: (id: number) => api.post<Notification>(`/notifications/notifications/${id}/mark_read/`),

  // Mark as unread
  markAsUnread: (id: number) => api.post<Notification>(`/notifications/notifications/${id}/mark_unread/`),

  // Mark all as read
  markAllAsRead: () => api.post<{ updated: number }>('/notifications/notifications/mark_all_read/'),

  // Clear all notifications
  clearAll: () => api.delete<{ deleted: number }>('/notifications/notifications/clear_all/'),

  // Get statistics
  getStats: () => api.get<{
    total: number
    unread: number
    by_category: Record<string, number>
    by_type: Record<string, number>
    by_priority: Record<string, number>
  }>('/notifications/notifications/stats/'),

  // Preferences
  getPreferences: () => api.get<NotificationPreference>('/notifications/preferences/my_preferences/'),
  updatePreferences: (data: Partial<NotificationPreference>) =>
    api.put<NotificationPreference>('/notifications/preferences/my_preferences/', data),
}

// Organizations API
// Data Export API
export const dataExportApi = {
  exportCandidates: (format: 'json' | 'csv' = 'json', includeCsv: boolean = false) => {
    const params = new URLSearchParams()
    params.append('format', format)
    if (includeCsv) {
      params.append('include_csv', 'true')
    }
    return api.get(`/data-export/candidates/?${params.toString()}`, {
      responseType: 'blob',
    })
  },
  exportOffers: (format: 'json' | 'csv' = 'json', includeCsv: boolean = false) => {
    const params = new URLSearchParams()
    params.append('format', format)
    if (includeCsv) {
      params.append('include_csv', 'true')
    }
    return api.get(`/data-export/offers/?${params.toString()}`, {
      responseType: 'blob',
    })
  },
  exportUsers: (format: 'json' | 'csv' = 'json', includeCsv: boolean = false) => {
    const params = new URLSearchParams()
    params.append('format', format)
    if (includeCsv) {
      params.append('include_csv', 'true')
    }
    return api.get(`/data-export/users/?${params.toString()}`, {
      responseType: 'blob',
    })
  },
  exportMatchings: (format: 'json' | 'csv' = 'json', includeCsv: boolean = false) => {
    const params = new URLSearchParams()
    params.append('format', format)
    if (includeCsv) {
      params.append('include_csv', 'true')
    }
    return api.get(`/data-export/matchings/?${params.toString()}`, {
      responseType: 'blob',
    })
  },
  exportHiringProcess: (format: 'json' | 'csv' = 'json', includeCsv: boolean = false) => {
    const params = new URLSearchParams()
    params.append('format', format)
    if (includeCsv) {
      params.append('include_csv', 'true')
    }
    return api.get(`/data-export/hiring-process/?${params.toString()}`, {
      responseType: 'blob',
    })
  },
  backupAllData: () => {
    return api.get('/data-export/backup/', {
      responseType: 'blob',
    })
  },
}

export const organizationsApi = {
  getAll: () => api.get<PaginatedResponse<Organization> | Organization[]>('/organizations/'),
  getById: (id: number) => api.get<Organization>(`/organizations/${id}/`),
  create: (data: CreateOrganizationData) => {
    const formData = new FormData()
    formData.append('name', data.name)
    if (data.description) formData.append('description', data.description)
    if (data.domain) formData.append('domain', data.domain)
    if (data.website) formData.append('website', data.website)
    if (data.logo && typeof data.logo !== 'string') formData.append('logo', data.logo)
    if (data.email) formData.append('email', data.email)
    if (data.phone) formData.append('phone', data.phone)
    if (data.address) formData.append('address', data.address)
    if (data.admin_email) formData.append('admin_email', data.admin_email)
    if (data.admin_setup_mode) formData.append('admin_setup_mode', data.admin_setup_mode)
    if (data.admin_password) formData.append('admin_password', data.admin_password)
    if (data.admin_password_confirm) formData.append('admin_password_confirm', data.admin_password_confirm)
    if (data.admin_name) formData.append('admin_name', data.admin_name)
    if (data.is_active !== undefined) formData.append('is_active', data.is_active.toString())
    if (data.number_of_users !== undefined) formData.append('number_of_users', data.number_of_users.toString())
    if (data.daily_cv_processing_limit !== undefined) formData.append('daily_cv_processing_limit', data.daily_cv_processing_limit.toString())
    if (data.daily_job_offer_limit !== undefined) formData.append('daily_job_offer_limit', data.daily_job_offer_limit.toString())
    if (data.subscription_plan !== undefined) formData.append('subscription_plan', data.subscription_plan || '')
    if (data.subscription_duration_type !== undefined) formData.append('subscription_duration_type', data.subscription_duration_type)
    if (data.subscription_duration_days !== undefined) formData.append('subscription_duration_days', data.subscription_duration_days.toString())
    if (data.subscription_deadline !== undefined) formData.append('subscription_deadline', data.subscription_deadline || '')
    if (data.ai_provider !== undefined) formData.append('ai_provider', data.ai_provider || '')
    if (data.ai_ollama_host !== undefined) formData.append('ai_ollama_host', data.ai_ollama_host || '')
    if (data.ai_openrouter_host !== undefined) formData.append('ai_openrouter_host', data.ai_openrouter_host || '')
    if (data.ai_model !== undefined) formData.append('ai_model', data.ai_model || '')
    if (data.ai_model_ocr !== undefined) formData.append('ai_model_ocr', data.ai_model_ocr || '')
    if (data.use_default_ai_token) {
      formData.append('use_default_ai_token', 'true')
    } else if (data.ai_token !== undefined) {
      formData.append('ai_token', data.ai_token || '')
    }
    if (data.allow_external_cv_ai !== undefined) {
      formData.append('allow_external_cv_ai', data.allow_external_cv_ai ? 'true' : 'false')
    }
    if (data.system_email_language !== undefined) formData.append('system_email_language', data.system_email_language)


    return api.post<Organization>('/organizations/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  update: (id: number, data: Partial<CreateOrganizationData>) => {
    const formData = new FormData()
    if (data.name) formData.append('name', data.name)
    if (data.description !== undefined) formData.append('description', data.description || '')
    if (data.domain) formData.append('domain', data.domain)
    if (data.website !== undefined) formData.append('website', data.website || '')
    // Handle logo: only append if it's a File object (new upload), not if it's a string (existing URL)
    // If logo is undefined/null, don't append anything (keeps existing logo)
    if (data.logo !== undefined && data.logo !== null) {
      if (typeof data.logo !== 'string' && data.logo instanceof File) {
        formData.append('logo', data.logo)
      }
      // If logo is explicitly set to null/empty string, we might want to clear it
      // But for now, we'll only update if a new file is provided
    }
    if (data.email !== undefined) formData.append('email', data.email || '')
    if (data.phone !== undefined) formData.append('phone', data.phone || '')
    if (data.address !== undefined) formData.append('address', data.address || '')
    if (data.is_active !== undefined) formData.append('is_active', data.is_active.toString())
    if (data.number_of_users !== undefined) formData.append('number_of_users', data.number_of_users.toString())
    if (data.daily_cv_processing_limit !== undefined) formData.append('daily_cv_processing_limit', data.daily_cv_processing_limit.toString())
    if (data.daily_job_offer_limit !== undefined) formData.append('daily_job_offer_limit', data.daily_job_offer_limit.toString())
    if (data.subscription_plan !== undefined) formData.append('subscription_plan', data.subscription_plan || '')
    if (data.subscription_duration_days !== undefined) formData.append('subscription_duration_days', data.subscription_duration_days.toString())
    if (data.subscription_deadline !== undefined) formData.append('subscription_deadline', data.subscription_deadline || '')
    if (data.ai_provider !== undefined) formData.append('ai_provider', data.ai_provider || '')
    if (data.ai_ollama_host !== undefined) formData.append('ai_ollama_host', data.ai_ollama_host || '')
    if (data.ai_openrouter_host !== undefined) formData.append('ai_openrouter_host', data.ai_openrouter_host || '')
    if (data.ai_model !== undefined) formData.append('ai_model', data.ai_model || '')
    if (data.ai_model_ocr !== undefined) formData.append('ai_model_ocr', data.ai_model_ocr || '')
    if (data.ai_token !== undefined) formData.append('ai_token', data.ai_token || '')
    if (data.allow_external_cv_ai !== undefined) {
      formData.append('allow_external_cv_ai', data.allow_external_cv_ai ? 'true' : 'false')
    }

    return api.patch<Organization>(`/organizations/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  delete: (id: number) => api.delete(`/organizations/${id}/`),
  getUsers: (id: number) => api.get(`/organizations/${id}/users/`),
  getStats: (id: number) => api.get<{
    users_count: number
    candidates_count: number
    offers_count: number
  }>(`/organizations/${id}/stats/`),
  getConsumptionStats: (id: number, range: string = 'month') => api.get<{
    history: Array<{
      date: string
      candidate_creation_count: number
      candidate_creation_invalid_count: number
      candidate_creation_deleted_count: number
      offer_creation_count: number
      offer_creation_deleted_count: number
      offer_modification_count: number
      offer_modification_deleted_count: number
      cost: number
      candidate_input_tokens: number
      candidate_output_tokens: number
      offer_input_tokens: number
      offer_output_tokens: number
    }>
    summary: Organization['consumption']
  }>(`/organizations/${id}/consumption_stats/`, { params: { range } }),
  getApiCost: (id: number) => api.get<{
    api_usage: number
    usage_daily: number
    usage_weekly: number
    usage_monthly: number
  }>(`/organizations/${id}/api_cost/`),
  recent_deleted_candidates: (id: number) => api.get(`/organizations/${id}/recent_deleted_candidates/`),
  recent_invalid_candidates: (id: number) => api.get(`/organizations/${id}/recent_invalid_candidates/`),
  recent_deleted_offers: (id: number) => api.get(`/organizations/${id}/recent_deleted_offers/`),
  recent_modified_offers: (id: number) => api.get(`/organizations/${id}/recent_modified_offers/`),
  validateInvitation: (token: string) =>
    api.post<OrganizationInvitationDetails>('/organizations/invitations/validate/', { token }),
  acceptInvitation: (data: { token: string; name: string; password: string; confirm_password: string }) =>
    api.post<{ message: string }>('/organizations/invitations/accept/', data),
  resendInvitation: (token: string, email: string) =>
    api.post<{ message: string }>('/organizations/invitations/resend/', { token, email }),
}

export const interviewAudioApi = {
  login: (email: string, access_code: string) => api.post<{
    message: string
    interview_id: number
    candidate_name: string
    candidate_photo: string | null
    offer_title: string
    questions: any
    status: string
    language: string
    is_deadline_passed?: boolean
    current_question_index?: number
    consent_given?: boolean
    consent_timestamp?: string | null
  }>('/interview-audio/candidate/login/', { email, access_code }),
  uploadVideoResponse: (interviewId: number, questionIndex: number, questionText: string, videoBlob: Blob, accessCode: string, duration?: number) => {
    const formData = new FormData()
    formData.append('question_index', questionIndex.toString())
    formData.append('question_text', questionText)
    formData.append('access_code', accessCode)
    formData.append('video_file', videoBlob, `question_${questionIndex}.webm`)
    if (duration) {
      formData.append('duration', duration.toString())
    }
    return api.post(`/interview-audio/interview/${interviewId}/upload_response/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  completeInterview: (interviewId: number, accessCode: string, formData?: FormData, onUploadProgress?: (progressEvent: any) => void) => {
    const config = {
      headers: formData ? { 'Content-Type': 'multipart/form-data' } : {},
      onUploadProgress
    }
    const payload = formData ?? new FormData()
    payload.append('access_code', accessCode)
    return api.post(`/interview-audio/interview/${interviewId}/complete/`, payload, config)
  },
  batchGenerateQuestions: (offerId: number, language?: string, number_of_questions: number = 10, questions_per_section?: Record<string, number>) =>
    api.post<{ message: string, results: any[], count: number }>('/interview-audio/audios/batch_generate_questions/', {
      offer_id: offerId,
      language,
      number_of_questions,
      questions_per_section
    }),
  createManualQuestions: (offerId: number, commonQuestions: CommonQuestion[], language: 'fr' | 'en' | 'custom') =>
    api.post<{ message: string, count: number, common_questions: CommonQuestion[], language: string }>('/interview-audio/audios/create_manual_questions/', {
      offer_id: offerId,
      common_questions: commonQuestions,
      language,
    }),
  startWithLanguage: (accessCode: string, language: string) =>
    api.post<{
      message: string
      interview_id: number
      language: string
      questions: any
      status: string
      current_question?: any
      total_questions_count?: number
    }>('/interview-audio/interview/start_with_language/', { access_code: accessCode, language }),
  getAll: (params?: { application__offer_id?: number, limit?: number, page_size?: number }) =>
    api.get<PaginatedResponse<any>>('/interview-audio/audios/', { params }),
  getStats: (params?: { organization?: number, created_after?: string }) =>
    api.get<{
      total_count: number
      generated_count: number
      completed_count: number
      interview_percentage: number
      interview_time_saved_minutes: number
      history: Array<{
        date: string
        interview_time_saved_minutes: number
      }>
    }>('/interview-audio/audios/stats/', { params }),
  getTranscriptionStatus: (interviewId: number, accessCode: string) =>
    api.get<{
      status: string
      total_segments: number
      transcribed_segments: number
      progress: number
    }>(`/interview-audio/interview/${interviewId}/transcription_status/`, {
      params: { access_code: accessCode }
    }),
  updateCurrentQuestion: (interviewId: number, currentQuestionIndex: number, accessCode: string) =>
    api.post(`/interview-audio/interview/${interviewId}/update_current_question/`, {
      current_question_index: currentQuestionIndex,
      access_code: accessCode
    }),
  getInterviewDetails: (interviewId: number, accessCode: string) =>
    api.get(`/interview-audio/interview/${interviewId}/get_details/`, {
      params: { access_code: accessCode }
    }),
  create: (data: { application: number; questions?: any }) =>
    api.post<any>('/interview-audio/audios/', data),
  update: (id: number, data: any) =>
    api.patch(`/interview-audio/audios/${id}/`, data),
  resetInterview: (interviewId: number) =>
    api.post(`/interview-audio/audios/${interviewId}/reset/`),
  delete: (id: number) =>
    api.delete(`/interview-audio/audios/${id}/`),
  sendInvitation: (interviewId: number) =>
    api.post<{ message: string }>(`/interview-audio/audios/${interviewId}/send_invitation/`),
  batchSendInvitations: (offerId: number) =>
    api.post<{ message: string, results: any[], count: number }>('/interview-audio/audios/batch_send_invitations/', { offer_id: offerId }),
  evaluateInterview: (interviewId: number) =>
    api.post<{ message: string, evaluation: any }>(`/interview-audio/audios/${interviewId}/evaluate/`),
  updateEvaluation: (interviewId: number, evaluation: any) =>
    api.post<{ message: string, evaluation: any }>(`/interview-audio/audios/${interviewId}/update_score/`, { evaluation }),
  batchEvaluateInterviews: (offerId: number) =>
    api.post<{ message: string, results: any[], count: number }>('/interview-audio/audios/batch_evaluate/', {
      offer_id: offerId
    }),
}

export default api
